import { secondsToMs } from '@thyme/libs/src/time/convert'
import { DateTime } from 'luxon'
import { onMounted, onUnmounted, ref } from 'vue'
import { useUpcomingCallsQuery, type UpcomingCall } from './upcomingCalls'

// How often we re-check stored calls' start times against the current time.
const TICK_INTERVAL_SECONDS = 30

// How far ahead of a call's start we announce it.
const REMINDER_LEAD_MINUTES = 5

/**
 * Keyed on id & start time, so a rescheduled call is able to re-announce
 * A `:day:` variant joins this when date-only calls start announcing.
 * @param call
 */
function announcementKey(call: UpcomingCall): string {
  if (call.isDateOnly) return `${call.communicationId}:day:${call.dueDate}`
  return `${call.communicationId}:at:${call.startTime.toISO()}`
}

/**
 * This owns the clock: the day window it fetches, and the lead-time comparison.
 * @param onCallsDue called with the calls that just came due, never with an empty array
 */
export function useUpcomingCallReminders(
  onCallsDue: (calls: UpcomingCall[]) => void
): void {
  const day = ref(DateTime.now())
  const { data: calls } = useUpcomingCallsQuery(day)
  // announcementKeys already handed to onCallsDue. Bounded by one session's calls
  const announced = new Set<string>()
  let tickHandle: ReturnType<typeof setInterval> | undefined

  /**
   * Announces any calls now within the lead time
   */
  function checkForDueCalls() {
    const now = DateTime.now()
    // Only reassign on a date change, so the query key stays stable within a day
    if (!now.hasSame(day.value, 'day')) day.value = now

    const callsDue = (calls.value ?? []).filter((call) => {
      // IMPROVEME(MET-3034): date-only calls announce on first action of the day, not off a start time
      if (call.isDateOnly) return false
      if (announced.has(announcementKey(call))) return false
      const minutesUntilStart = call.startTime.diff(now).as('minutes')
      // Forward-looking only: a call whose start has passed is ignored (may need adjustment for all day calls in future)
      return (
        minutesUntilStart >= 0 && minutesUntilStart <= REMINDER_LEAD_MINUTES
      )
    })

    if (!callsDue.length) return

    for (const call of callsDue) announced.add(announcementKey(call))
    onCallsDue(callsDue)
  }

  onMounted(() => {
    // Check on mount too, so a call already inside the lead time isn't held up
    checkForDueCalls()
    tickHandle = setInterval(
      checkForDueCalls,
      secondsToMs(TICK_INTERVAL_SECONDS)
    )
  })

  onUnmounted(() => {
    clearInterval(tickHandle)
  })
}
